import { ChevronRight } from 'react-bootstrap-icons';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Row, Skeleton, Typography } from 'antd';

import ImageRatio from '@/components/shared/ImageRatio';
import { useAppSelector } from '@/hooks/redux';
import { allowedStatus, blockedStatus, enStatus } from '@/interfaces/course';
import { useGetLastLessonWatchedQuery, coursesApi } from '@/services/course';
import styled, { css, StyledProp } from '@/styled';

const KeepLearning = (props: StyledProp & { courseStatus: enStatus }) => {
  const { hash } = useParams();
  const lastLessonWatched = useGetLastLessonWatchedQuery(hash);
  const modules = coursesApi.endpoints.getProgress.useQueryState(hash);
  const navigate = useNavigate();
  const user = useAppSelector(({ auth }) => auth.user);
  const isLoading = lastLessonWatched.isLoading || modules.isLoading;

  if (isLoading) {
    return (
      <div className={props.className}>
        <Skeleton.Image />

        <div className='sideContent'>
          <Skeleton.Input size='small' />
          <Skeleton.Input size='small' />
          <Skeleton.Button />
        </div>
      </div>
    );
  }

  const isStatusAllowed = allowedStatus.includes(props.courseStatus) && !isLoading;
  const lesson = modules?.data
    ?.flatMap(module => module.lessons)
    .find(lesson => lesson?.id === lastLessonWatched?.data?.id);

  if (!lesson || !isStatusAllowed || !user?.id) {
    return null;
  }

  const goToLesson = () => navigate(`/curso/${hash}/aula/${lesson?.id}`);

  return (
    <Row className={props.className}>
      <ImageRatio aspect='16:9' src={lesson?.image} onClick={goToLesson} />

      <div className='sideContent'>
        <Typography.Title level={3} ellipsis={{ rows: 2 }} id='lesson-title'>
          {lesson?.title}
        </Typography.Title>
        <Button
          onClick={goToLesson}
          type='primary'
          aria-label='Botão iniciar aula de onde parou'
          disabled={blockedStatus.includes(lesson.status)}
          size='large'
          id='btn-go-to-lesson'
        >
          {blockedStatus.includes(lesson.status) && 'Aula bloqueada'}
          {!blockedStatus.includes(lesson.status) && 'Continuar estudando'}
          <ChevronRight />
        </Button>
      </div>
    </Row>
  );
};

export default styled(KeepLearning)(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: 359px auto;
    grid-column-gap: ${theme.space.pxToRem(40)};
    grid-row-gap: ${theme.space.pxToRem(8)};
    margin-bottom: ${theme.space.pxToRem(64)};

    ${theme.breakpoints.down('sm')} {
      margin-top: ${theme.space.pxToRem(56)};
      margin-bottom: ${theme.space.pxToRem(4)};
      grid-template-columns: auto;

      button {
        min-width: 100%;
        justify-content: center;
      }
    }

    h3.ant-typography {
      margin: 0;
    }

    .ant-skeleton-image {
      width: 356px !important;
      height: 200px !important;

      ${theme.breakpoints.down('md')} {
        width: 288px !important;
        height: 200px !important;
      }
    }

    .ant-skeleton-input {
      width: 356px !important;

      ${theme.breakpoints.down('md')} {
        width: 288px !important;
      }
    }

    button {
      max-width: 13em;
      display: flex;
      align-items: center;
      column-gap: ${theme.space.pxToRem(8)};
    }

    .sideContent {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: ${theme.space.pxToRem(24)};

      ${theme.breakpoints.down('sm')} {
        gap: ${theme.space.pxToRem(16)};
      }
    }
  `
);
